import { ApolloProvider } from '@apollo/client'
import { useContext, createContext, useMemo, useEffect, useState, useReducer, useCallback } from 'react'

import { initApolloClient } from '@/network/graphql/apollo/apolloClient'

import { logger, LOGGER_LEVEL } from '../network/common/logger'

import { useAppStatus } from './firebase/Firebase.provider'

import type { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import type { ReactNode } from 'react'

type NetworkGateawayContext = {
  apolloClient: ApolloClient<NormalizedCacheObject>
  hasEncounteredNetworkError: boolean
  hasEncounteredInternalError: boolean
  showWebsocketStatus: boolean
  websocketStatus: WS_STATUS
  nextTimeBeforeRetry: number
}

type NetworkGateawayProviderProps = {
  children: ReactNode
}

export const MAX_TIME_BEFORE_RETRY = 30000

export enum WS_STATUS {
  CONNECTED = 'connected',
  CLOSED = 'closed',
  CONNECTING = 'connecting',
  INIT = 'init',
}

let timedOut: NodeJS.Timeout
let isMounted = false
const NetworkGateawayContext = createContext<NetworkGateawayContext>({} as NetworkGateawayContext)

export const NetworkGateawayProvider = ({ children }: NetworkGateawayProviderProps) => {
  const { logsAreEnabled } = useAppStatus()

  const [hasEncounteredNetworkError, raiseNetworkError] = useReducer(() => true, false)
  const [hasEncounteredInternalError, raiseInternalError] = useReducer(() => true, false)
  const [websocketStatus, setWebsocketStatus] = useState(WS_STATUS.INIT)
  const [showWebsocketStatus, setShowWebsocketStatus] = useState(false)
  const [nextTimeBeforeRetry, setNextTimeBeforeRetry] = useReducer((time) => time, 0)

  const apolloClient = useMemo(
    () => initApolloClient({ raiseNetworkError, raiseInternalError, setNextTimeBeforeRetry }),
    []
  )

  const handleOpened = useCallback(() => {
    logger({
      level: LOGGER_LEVEL.INFO,
      message: 'Websocket connected',
      meta: {
        eventType: 'WEBSOCKET_OPENED',
      },
      shouldSendToServer: logsAreEnabled,
    })

    setTimeout(() => setShowWebsocketStatus(false), 5000)

    if (isMounted) {
      apolloClient.refetchQueries({
        include: 'active',
      })
    } else {
      isMounted = true
    }

    setWebsocketStatus(WS_STATUS.CONNECTED)
  }, [websocketStatus, apolloClient, logsAreEnabled])

  const handleConnected = () => {
    logger({
      level: LOGGER_LEVEL.INFO,
      message: 'Websocket connected',
      meta: {
        eventType: 'WEBSOCKET_OPENED',
      },
      shouldSendToServer: logsAreEnabled,
    })

    setTimeout(() => setShowWebsocketStatus(false), 5000)
    if (websocketStatus === WS_STATUS.CONNECTING) {
      apolloClient.refetchQueries({
        include: 'active',
      })
    }
    setWebsocketStatus(WS_STATUS.CONNECTED)
  }

  const handleClosed = (event: unknown) => {
    logger({
      level: LOGGER_LEVEL.INFO,
      message: 'Websocket closed',
      meta: {
        eventType: 'WEBSOCKET_CLOSED',
      },
      shouldSendToServer: logsAreEnabled,
    })

    if (!(event as CloseEvent).wasClean) {
      setShowWebsocketStatus(true)
      setTimeout(() => setShowWebsocketStatus(false), 5000)
    }
    setWebsocketStatus(WS_STATUS.CLOSED)
  }

  const handleConnecting = () => {
    setWebsocketStatus(WS_STATUS.CONNECTING)
    setShowWebsocketStatus(true)
    logger({ level: LOGGER_LEVEL.INFO, message: 'Websocket connecting', shouldSendToServer: logsAreEnabled })
  }

  const handlePing = (received: boolean) => {
    logger({ level: LOGGER_LEVEL.DEBUG, message: 'Websocket ping', shouldSendToServer: logsAreEnabled })
    if (!received) {
      timedOut = setTimeout(() => {
        const websocket = window.__VOGGT__WS_CLIENT__
        if (websocketStatus === WS_STATUS.CONNECTED) {
          websocket.dispose()
        }
        logger({
          level: LOGGER_LEVEL.INFO,
          message: 'Websocket ping timeout, closing the socket',
          meta: {
            eventType: 'WEBSOCKET_PING_TIMEOUT',
          },
          shouldSendToServer: logsAreEnabled,
        })
        setWebsocketStatus(WS_STATUS.CONNECTING)
        setShowWebsocketStatus(true)
      }, 3_000)
    }
  }

  const handlePong = (received: boolean) => {
    logger({ level: LOGGER_LEVEL.DEBUG, message: 'Websocket pong', shouldSendToServer: logsAreEnabled })
    if (received) {
      clearTimeout(timedOut)
    }
  }

  useEffect(() => {
    const wsClient = window.__VOGGT__WS_CLIENT__
    if (!wsClient) {
      return
    }

    const cleanupFns = [
      wsClient.on('opened', handleOpened),
      wsClient.on('closed', handleClosed),
      wsClient.on('connected', handleConnected),
      wsClient.on('connecting', handleConnecting),
      wsClient.on('ping', handlePing),
      wsClient.on('pong', handlePong),
    ]

    return () => {
      cleanupFns.forEach((cleanup) => cleanup())
    }
  }, [apolloClient, handleOpened, logsAreEnabled])

  const value: NetworkGateawayContext = useMemo(
    () => ({
      apolloClient,
      hasEncounteredNetworkError,
      hasEncounteredInternalError,
      showWebsocketStatus,
      websocketStatus,
      nextTimeBeforeRetry,
    }),
    [
      apolloClient,
      hasEncounteredNetworkError,
      hasEncounteredInternalError,
      websocketStatus,
      showWebsocketStatus,
      nextTimeBeforeRetry,
    ]
  )

  return (
    <ApolloProvider client={apolloClient}>
      <NetworkGateawayContext.Provider value={value}>{children}</NetworkGateawayContext.Provider>
    </ApolloProvider>
  )
}

export const useNetworkGateaway = () => useContext(NetworkGateawayContext)
